import { IonCol, IonContent, IonGrid, IonHeader, IonMenu, IonRow, IonTitle, IonToolbar } from "@ionic/react";
import { ComponentProps, PropsWithChildren, ReactNode, RefObject } from "react";
import { useIntl } from "react-intl";

import "@components/DataUnread.module.css";
import CircleButton from "@components/buttons/CircleButton";
import styles from "@components/menus/Menu.module.css";
import Icon from "@components/text/Icon";
import InternalLink from "@components/typography/InternalLink";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMyExpert from "@hooks/useMyExpert";
import useUnreadMessagesCount from "@hooks/useUnreadMessagesCount";
import { OpenerTypeEnum } from "@typing/Enums";
import { journeyRoute } from "@utils/routeUtils";

type Props = Pick<ComponentProps<typeof IonMenu>, "className"> &
  PropsWithChildren<{
    hidden?: boolean;
    journeyId: string;
    leftSection?: ReactNode;
    menuRef: RefObject<HTMLIonMenuElement>;
    mobileHidden?: boolean;
    showMessagesButton?: boolean;
  }>;

const Menu = ({
  children,
  className,
  hidden = false,
  journeyId,
  leftSection,
  menuRef,
  mobileHidden = false,
  showMessagesButton = false
}: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const messagesOpener = useGlobalOpener(OpenerTypeEnum.MESSAGES);
  const unreadMessageCountData = useUnreadMessagesCount(journeyId);

  const classNames = [styles.menu];
  if (hidden) classNames.push(styles.hidden);
  if (mobileHidden) classNames.push(styles.mobileHidden);
  if (className) classNames.push(className);

  return (
    <IonMenu className={classNames.join(" ")} contentId="main" data-test="menu" ref={menuRef}>
      <IonGrid className="ion-no-padding ion-no-margin">
        <IonRow>
          {leftSection && (
            <IonCol className={styles.leftSection} size="auto">
              <IonContent className={styles.leftSectionWidth}>{leftSection}</IonContent>
            </IonCol>
          )}
          <IonContent className={styles.menuContent}>
            <IonCol>
              <IonGrid>
                <IonRow className="flex-column">
                  <IonCol>
                    <IonHeader className={`${styles.header} ion-no-border`} role="">
                      <IonToolbar className={styles.toolbar}>
                        <IonTitle>
                          <div className={styles.logoContainer}>
                            <InternalLink
                              aria-label={intl.formatMessage({ id: "menus.client.plan" })}
                              expand
                              linkEventCreateVariables={{ journeyId }}
                              stylized={false}
                              to={journeyRoute({
                                journeyId,
                                linkAfterJourney: myExpert ? "/care-plan" : "/home",
                                myExpert
                              })}
                            >
                              <Icon color="white" icon="logoIcon" size="massive" />
                            </InternalLink>
                          </div>
                        </IonTitle>
                      </IonToolbar>
                    </IonHeader>

                    {children}
                  </IonCol>
                  {showMessagesButton && (
                    <IonCol className="full-width ion-padding flex items-center justify-center" size="auto">
                      <CircleButton
                        {...unreadMessageCountData}
                        ariaLabelKey="accessibility.ariaLabels.toggleMessages"
                        color="primary-500"
                        icon="message"
                        onClick={messagesOpener.toggle}
                        size="menu"
                      />
                    </IonCol>
                  )}
                </IonRow>
              </IonGrid>
            </IonCol>
            <IonCol size="auto"></IonCol>
          </IonContent>
        </IonRow>
      </IonGrid>
    </IonMenu>
  );
};

export default Menu;
